import { authRefreshApi } from "../apis/authApi";
import { useAuthStore } from "../state/useAuthStore";
import {
  getAccessTokenFromStorage,
  getRefreshTokenFromStorage,
} from "./tokenStorage";
import { redirectToLogin } from "./authUtils";
import { sanitizeErrorForLog } from "./sanitizeErrorForLog";

/**
 * Shared token refresh manager to prevent race conditions.
 * Ensures only ONE token refresh request happens at a time across the entire app.
 */

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

class TokenRefreshManager {
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;
  private queue: QueueItem[] = [];

  /**
   * Refresh the access token. If a refresh is already in progress,
   * queues the request and waits for the refresh to complete.
   * 
   * @param reason - Why the refresh is being triggered (for logging)
   * @returns The new access token
   * @throws Error if refresh fails
   */
  async refreshAccessToken(_reason: string = "unknown"): Promise<string> {
    // If already refreshing, queue this request
    if (this.isRefreshing && this.refreshPromise) {
      return new Promise<string>((resolve, reject) => {
        this.queue.push({ resolve, reject });
      });
    }

    // Start a new refresh
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const newToken = await this.refreshPromise;
      this.processQueue(null, newToken);
      return newToken;
    } catch (error) {
      console.error("[TokenRefreshManager] Refresh failed", sanitizeErrorForLog(error));
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Actually performs the token refresh API call.
   * Includes retry logic for race conditions.
   */
  private async performRefresh(): Promise<string> {
    const accessToken = getAccessTokenFromStorage();
    const refreshToken = getRefreshTokenFromStorage();

    // Validate tokens before making the request
    if (!accessToken || !refreshToken) {
      console.error("[TokenRefreshManager] Missing tokens in storage", {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
      });
      
      // Clean up and redirect
      useAuthStore.getState().logout();
      redirectToLogin("Missing tokens in storage");
      throw new Error("Missing tokens");
    }

    // Validate tokens are not empty strings
    if (accessToken.trim() === "" || refreshToken.trim() === "") {
      console.error("[TokenRefreshManager] Empty tokens in storage");
      useAuthStore.getState().logout();
      redirectToLogin("Empty tokens in storage");
      throw new Error("Empty tokens");
    }

    try {
      const data = await authRefreshApi({ accessToken, refreshToken });
      
      if (!data.accessToken || !data.refreshToken) {
        throw new Error("Refresh response missing tokens");
      }

      // Update store with new tokens
      useAuthStore.getState().login(data.accessToken, data.refreshToken);

      return data.accessToken;
    } catch (error: any) {
      console.error("[TokenRefreshManager] Refresh API call failed", {
        status: error?.response?.status,
        message: error?.message,
      });

      // Check if this is a 401 (token invalid/expired/rotated)
      if (error?.response?.status === 401) {
        console.warn("[TokenRefreshManager] Refresh token invalid, attempting retry with latest tokens from storage");
        
        // Race condition scenario: tokens might have been updated by another request
        // Try once more with the latest tokens from storage
        const latestAccessToken = getAccessTokenFromStorage();
        const latestRefreshToken = getRefreshTokenFromStorage();
        
        // Only retry if tokens have changed (another request refreshed them)
        if (latestAccessToken !== accessToken || latestRefreshToken !== refreshToken) {
          try {
            const retryData = await authRefreshApi({ 
              accessToken: latestAccessToken, 
              refreshToken: latestRefreshToken 
            });
            
            if (retryData.accessToken && retryData.refreshToken) {
              useAuthStore.getState().login(retryData.accessToken, retryData.refreshToken);
              return retryData.accessToken;
            }
          } catch (retryError: any) {
            console.error("[TokenRefreshManager] Retry also failed", sanitizeErrorForLog(retryError));
            // Fall through to logout
          }
        }
        
        // If we get here, refresh truly failed - logout and redirect
        console.warn("[TokenRefreshManager] Refresh token invalid or rotated, logging out");
        useAuthStore.getState().logout();
        redirectToLogin("Refresh token invalid or expired");
      }

      throw error;
    }
  }

  /**
   * Process all queued requests after a refresh completes.
   */
  private processQueue(error: any, token: string | null): void {
    this.queue.forEach((item) => {
      if (error) {
        item.reject(error);
      } else if (token) {
        item.resolve(token);
      }
    });
    this.queue = [];
  }

  /**
   * Check if a refresh is currently in progress.
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }

  /**
   * Clear the queue (used for testing or cleanup).
   */
  clearQueue(): void {
    this.queue = [];
  }
}

// Export a singleton instance
export const tokenRefreshManager = new TokenRefreshManager();
